import * as React from 'react';
import styled from 'styled-components';
import Text11 from '@sdk/components/ui/typography/Text11';
import dynamicLaborValue from '@sdk/labor/helpers/dynamicLaborValue';
import displayDuration from '@sdk/utils/displayDuration';
import useAuth from '@sdk/authentication/useAuth';
import { useCurrency } from '@sdk/common/currency/CurrencyContext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import EllipsisStyle from '@sdk/components/ui/EllipsisStyle';
import type { LaborUISchemaType } from '@sdk/labor/types';
import type { CurrencyType } from '@sdk/common/currency/types/CurrencyType';

import type { User } from '../../types/GroupsArrayType';
import type { OptionsFilterType } from '../../types';
import consolidateTotals from '../helpers/consolidateTotals';
import Tooltip from '../../../components/Tooltip';

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  ${EllipsisStyle};
`;

type EllipsisTextProps = {
  inactive: boolean;
  title: string;
};
const EllipsisText = styled(Text11)<EllipsisTextProps>`
  ${EllipsisStyle};
  text-decoration: ${({ inactive }): string => (inactive ? 'line-through' : 'none')};
`;

const Pointer = styled.span`
  cursor: pointer;
`;

type DisplayLaborProps = {
  isRed: boolean;
  user: User;
  optionsFilter: OptionsFilterType;
  laborUISchema: LaborUISchemaType;
  currency: CurrencyType;
};
const DisplayLabor: React.FC<React.PropsWithChildren<DisplayLaborProps>> = ({
  isRed,
  user,
  optionsFilter,
  laborUISchema,
  currency,
}) => {
  const { userRow } = laborUISchema.schema.calendarUISchema;
  const items = userRow.items.filter((item) => laborUISchema.evalIf(item, optionsFilter));
  const inactive = user.deleted || !user.active;

  return (
    <>
      {items.map((item, index) => {
        const { unit, property } = item;
        const value = user.totals[property];
        let displayValue;
        if (!optionsFilter.showForeignShifts) {
          displayValue = dynamicLaborValue({ value, unit, currency });
        } else {
          const valueWithForeign = user.totals[property] + user.foreignTotals[property];
          displayValue = `${dynamicLaborValue({ value, unit, currency })} / ${dynamicLaborValue({
            value: valueWithForeign,
            unit,
            currency,
          })}`;
        }

        return (
          <EllipsisText
            key={index}
            themeColor={isRed ? ThemeColorType.danger : ThemeColorType.iconGray}
            bold
            inactive={inactive}
            title={displayValue}>
            {displayValue}
          </EllipsisText>
        );
      })}
    </>
  );
};

type DisplayDurationProps = {
  isRed: boolean;
  showForeignShifts: boolean;
  user: User;
};
const DisplayDuration: React.FC<React.PropsWithChildren<DisplayDurationProps>> = ({
  isRed,
  showForeignShifts,
  user,
}) => {
  const { paidMinutes } = user.totals;
  const { paidMinutes: foreignPaidMinutes } = user.foreignTotals;
  const inactive = user.deleted || !user.active;

  const value = showForeignShifts
    ? `${displayDuration(paidMinutes)} / ${displayDuration(paidMinutes + foreignPaidMinutes)}`
    : displayDuration(paidMinutes);

  return (
    <EllipsisText
      themeColor={isRed ? ThemeColorType.danger : ThemeColorType.iconGray}
      bold
      inactive={inactive}
      title={value}>
      {value}
    </EllipsisText>
  );
};

type Props = {
  user: User;
  laborUISchema: LaborUISchemaType;
  optionsFilter: OptionsFilterType;
};
const UserCellSummary: React.FC<React.PropsWithChildren<Props>> = ({
  user,
  laborUISchema,
  optionsFilter,
}) => {
  const { currentUser } = useAuth();
  const { currency } = useCurrency();
  const { isManager, hasAccessToLaborCost, orgSettings } = currentUser;
  const canSeeTotals = user.isMe || isManager || orgSettings.usersSeeHoursOfOthers;

  let isRed = false;
  if (hasAccessToLaborCost) {
    const { userRow } = laborUISchema.schema.calendarUISchema;
    isRed = Boolean(
      hasAccessToLaborCost &&
        laborUISchema.evalColor(userRow, consolidateTotals(user), optionsFilter).red
    );
  }

  const displayNoAccessToLabor = (): JSX.Element => {
    return (
      <Row>
        {canSeeTotals && optionsFilter.showCountOfHours && (
          <>
            <DisplayDuration
              isRed={isRed}
              showForeignShifts={optionsFilter.showForeignShifts}
              user={user}
            />
            <Text11 bold themeColor={ThemeColorType.iconGray}>
              &nbsp;•&nbsp;
            </Text11>
          </>
        )}
        <Pointer>
          <Text11
            themeColor={ThemeColorType.separatorOnGray}
            bold
            data-tip="Tracking of labor costs is available in premium"
            data-place="top">
            {dynamicLaborValue({ value: 0, unit: 'currency', currency })}
          </Text11>
        </Pointer>
        <Tooltip attachToBody />
      </Row>
    );
  };

  const displayHasAccessToLabor = (): JSX.Element => {
    return (
      <>
        <Row>
          {canSeeTotals && (
            <>
              {optionsFilter.showCountOfHours && (
                <DisplayDuration
                  isRed={isRed}
                  showForeignShifts={optionsFilter.showForeignShifts}
                  user={user}
                />
              )}
              {!optionsFilter.showForeignShifts && (
                <>
                  {optionsFilter.showLaborCost && optionsFilter.showCountOfHours && (
                    <Text11 bold themeColor={ThemeColorType.iconGray}>
                      &nbsp;•&nbsp;
                    </Text11>
                  )}
                  <DisplayLabor
                    isRed={isRed}
                    user={user}
                    optionsFilter={optionsFilter}
                    laborUISchema={laborUISchema}
                    currency={currency}
                  />
                </>
              )}
            </>
          )}
        </Row>
        {optionsFilter.showForeignShifts && (
          <Row>
            <DisplayLabor
              isRed={isRed}
              user={user}
              optionsFilter={optionsFilter}
              laborUISchema={laborUISchema}
              currency={currency}
            />
          </Row>
        )}
      </>
    );
  };

  return hasAccessToLaborCost ? displayHasAccessToLabor() : displayNoAccessToLabor();
};

export default UserCellSummary;
